// localStorage persistence for favorites. Everything here is defensive because storage
// may be unavailable (private mode, quota, SSR-ish test envs) or hold garbage (a user
// edited it, a previous version wrote something else): reads degrade to "no favorites",
// writes never throw. The storage backend is injected as a seam so tests can pass a
// mock.

// Bump the suffix if the on-disk shape ever changes.
export const FAVORITES_KEY = "devconf.favorites.v1";

// Accessing localStorage (not just using it) throws in older Safari private mode, so
// probe it inside a try.
function defaultStorage() {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

export function readFavoriteIds(storage = defaultStorage()) {
  if (!storage) {
    return [];
  }
  try {
    const raw = storage.getItem(FAVORITES_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((n) => Number.isInteger(n));
  } catch {
    return [];
  }
}

export function writeFavoriteIds(ids, storage = defaultStorage()) {
  if (!storage) {
    return;
  }
  try {
    storage.setItem(FAVORITES_KEY, JSON.stringify(ids.filter((n) => Number.isInteger(n))));
  } catch {
    /* ignore: storage may be full or unavailable */
  }
}

// Only writes when the `favorites` Set IDENTITY changes (the reducer copy-on-writes
// it), so unrelated dispatches (day changes, loads, filters) don't touch storage.
export function initFavoritesPersistence(store, storage = defaultStorage()) {
  const saved = readFavoriteIds(storage);
  if (saved.length > 0) {
    store.dispatch({ type: "favorites/hydrate", ids: saved });
  }

  let previous = store.getState().favorites;
  return store.subscribe((state) => {
    if (state.favorites === previous) {
      return;
    }
    previous = state.favorites;
    writeFavoriteIds([...state.favorites], storage);
  });
}
